import { useEffect } from "react";
import { useToast } from "@/components/ui/use-toast";
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "@/components/ui/toast";

// Default time a toast stays on screen before auto-dismissing. The
// underlying `use-toast` stub doesn't manage this on its own (it was a
// stripped-down copy of the shadcn template that left Radix's auto-close
// behaviour unwired), so the Toaster owns the timer.
const TOAST_AUTO_DISMISS_MS = 4000;

export function Toaster() {
  const { toasts, dismiss } = useToast();

  // Auto-dismiss every open toast after a few seconds so they don't pile
  // up on the screen. Each toast gets its own timer keyed by id.
  useEffect(() => {
    const timers = toasts
      .filter((t) => t.open !== false)
      .map((t) =>
        setTimeout(() => dismiss(t.id), t.duration ?? TOAST_AUTO_DISMISS_MS),
      );
    return () => timers.forEach(clearTimeout);
  }, [toasts, dismiss]);

  return (
    <ToastProvider>
      {toasts
        .filter((t) => t.open !== false)
        .map(function ({ id, title, description, action, ...props }) {
          return (
            <Toast key={id} {...props}>
              <div className="grid gap-1">
                {title && <ToastTitle>{title}</ToastTitle>}
                {description && (
                  <ToastDescription>{description}</ToastDescription>
                )}
              </div>
              {action}
              <ToastClose
                onClick={() => dismiss(id)}
                data-testid={`toast-close-${id}`}
              />
            </Toast>
          );
        })}
      <ToastViewport />
    </ToastProvider>
  );
}
